/**
 * WorkoutZoneProfile Component
 * Displays a visual elevation-style profile of workout zones over time
 * Similar to Garmin/TrainingPeaks workout visualizations
 *
 * Supports:
 * - Running workouts (zone-based 1-5)
 * - Indoor cycling workouts (FTP percentage-based)
 */

import { Box, Tooltip, useTheme } from '@mui/material';
import type { SportType, WorkoutStep } from '@shared/types';
import React, { useMemo } from 'react';

interface WorkoutZoneProfileProps {
  steps: WorkoutStep[];
  height?: number;
  showLabels?: boolean;
  /** Sport type - affects how intensity is calculated and displayed */
  sportType?: SportType;
  /** Environment - 'indoor' cycling uses FTP% */
  environment?: string;
  /** Callback when hovering over a step segment */
  onHoverStep?: (stepIndex: number | null) => void;
  /** Currently hovered step index (for external highlighting) */
  hoveredStepIndex?: number | null;
  /** FTP value for power display in tooltips */
  ftp?: number;
}

interface FlattenedStep {
  zone: number;
  duration: number;
  /** If true, this is part of a ramp and should show gradient effect */
  isRamp?: boolean;
  /** For ramp steps: the target zone at end of ramp */
  rampEndZone?: number;
  /** FTP percentage for indoor cycling */
  ftpPercent?: number;
  /** End FTP percentage for ramps */
  rampEndFtpPercent?: number;
}

// Zone colors matching the training zone definitions
const ZONE_COLORS = {
  1: '#4CAF50', // Green - Recovery
  2: '#8BC34A', // Light Green - Easy/Base
  3: '#FFC107', // Amber - Tempo
  4: '#FF9800', // Orange - Threshold
  5: '#F44336', // Red - VO2max
};

// Zone heights as percentage (zone 5 = 100%, zone 1 = 20%)
const ZONE_HEIGHTS: Record<number, number> = {
  1: 25,
  2: 40,
  3: 60,
  4: 80,
  5: 100,
};

/**
 * Convert FTP percentage to visual height (0-100%)
 * FTP range: 50% (easy) to 120% (VO2max+)
 * Visual: 50% FTP = 15% height, 120% FTP = 100% height
 */
function ftpPercentToHeight(ftpPercent: number): number {
  // Clamp between 50 and 120
  const clamped = Math.max(50, Math.min(120, ftpPercent));
  // Map 50-120 to 15-100
  return 15 + ((clamped - 50) / 70) * 85;
}

/**
 * Get color based on FTP percentage (Coggan power zones)
 */
function getFtpColor(ftpPercent: number): string {
  if (ftpPercent < 56) return '#9E9E9E'; // Recovery - grey
  if (ftpPercent < 76) return '#4CAF50'; // Active Recovery/Endurance - green
  if (ftpPercent < 90) return '#8BC34A'; // Endurance - light green
  if (ftpPercent < 95) return '#FFC107'; // Tempo/Sweet Spot - amber
  if (ftpPercent < 106) return '#FF9800'; // Threshold - orange
  return '#F44336'; // VO2max+ - red
}

/**
 * Flatten nested workout steps (handle repeat blocks and ramps)
 * Supports both zone-based (running) and FTP%-based (indoor cycling) workouts
 */
function flattenSteps(steps: WorkoutStep[], isIndoorCycling: boolean = false): FlattenedStep[] {
  const flattened: FlattenedStep[] = [];

  for (const step of steps) {
    if (step.repeat && step.steps) {
      // Repeat block - expand the nested steps
      for (let i = 0; i < step.repeat; i++) {
        flattened.push(...flattenSteps(step.steps, isIndoorCycling));
      }
    } else if (
      isIndoorCycling &&
      step.ramp &&
      step.duration &&
      step.ramp.startFtpPercent !== undefined &&
      step.ramp.endFtpPercent !== undefined
    ) {
      // Indoor cycling FTP ramp - split into segments for visual gradient effect
      const startFtp = step.ramp.startFtpPercent;
      const endFtp = step.ramp.endFtpPercent;
      const ftpDiff = Math.abs(endFtp - startFtp);
      const numSegments = Math.max(2, Math.ceil(ftpDiff / 10)); // At least 2 segments, more for larger ranges
      const segmentDuration = step.duration / numSegments;
      const ftpStep = (endFtp - startFtp) / numSegments;

      for (let i = 0; i < numSegments; i++) {
        const currentFtp = startFtp + i * ftpStep;
        const nextFtp = startFtp + (i + 1) * ftpStep;
        flattened.push({
          zone: 0, // Not used for FTP-based
          duration: segmentDuration,
          ftpPercent: currentFtp,
          isRamp: true,
          rampEndFtpPercent: nextFtp,
        });
      }
    } else if (isIndoorCycling && step.duration && step.ftpPercent !== undefined) {
      // Indoor cycling with FTP percentage
      const ftpValue =
        typeof step.ftpPercent === 'number'
          ? step.ftpPercent
          : (step.ftpPercent.min + step.ftpPercent.max) / 2;
      flattened.push({
        zone: 0, // Not used for FTP-based
        duration: step.duration,
        ftpPercent: ftpValue,
      });
    } else if (
      step.ramp &&
      step.duration &&
      step.ramp.startZone !== undefined &&
      step.ramp.endZone !== undefined
    ) {
      // Ramp step - split into segments for visual gradient effect
      const startZone = step.ramp.startZone;
      const endZone = step.ramp.endZone;
      const numSegments = Math.abs(endZone - startZone) + 1;
      const segmentDuration = step.duration / numSegments;
      const direction = endZone > startZone ? 1 : -1;

      for (let i = 0; i < numSegments; i++) {
        const currentZone = startZone + i * direction;
        flattened.push({
          zone: currentZone,
          duration: segmentDuration,
          isRamp: true,
          rampEndZone: endZone,
        });
      }
    } else if (step.duration && step.zone) {
      flattened.push({
        zone: step.zone,
        duration: step.duration,
      });
    } else if (step.distance && step.zone) {
      // For distance-based steps, estimate duration (assume ~5min/km pace)
      const estimatedDuration = (step.distance / 1000) * 5 * 60;
      flattened.push({
        zone: step.zone,
        duration: estimatedDuration,
      });
    } else if (step.duration) {
      // Step has duration but no zone - default to zone 2
      flattened.push({
        zone: 2,
        duration: step.duration,
      });
    }
  }

  return flattened;
}

export const WorkoutZoneProfile: React.FC<WorkoutZoneProfileProps> = ({
  steps,
  height = 60,
  showLabels: _showLabels = false, // kept for backwards compatibility
  sportType,
  environment,
  onHoverStep,
  hoveredStepIndex,
  ftp = 200, // Default FTP for power calculations
}) => {
  // Suppress unused warning for backwards compatibility
  void _showLabels;
  const theme = useTheme();

  /**
   * Generate tooltip content with power in Watts
   */
  const getTooltipContent = (
    step: FlattenedStep,
    isRamp: boolean = false,
    rampEndFtp?: number
  ): string => {
    if (step.ftpPercent !== undefined) {
      const power = Math.round((step.ftpPercent / 100) * ftp);
      const duration = Math.round(step.duration / 60);
      if (isRamp && rampEndFtp !== undefined) {
        const endPower = Math.round((rampEndFtp / 100) * ftp);
        return `${Math.round(step.ftpPercent)}% → ${Math.round(rampEndFtp)}% FTP\n${power}W → ${endPower}W\n${duration}min`;
      }
      return `${Math.round(step.ftpPercent)}% FTP\n${power}W\n${duration}min`;
    }
    // Zone-based
    const duration = Math.round(step.duration / 60);
    if (isRamp && step.rampEndZone) {
      return `Zone ${step.zone} → Zone ${step.rampEndZone}\n${duration}min`;
    }
    return `Zone ${step.zone}\n${duration}min`;
  };

  // Helper to check if steps contain FTP-based structure (indicating indoor cycling)
  const hasFtpBasedSteps = (stepList: WorkoutStep[]): boolean => {
    return stepList.some(step => {
      if (step.ftpPercent !== undefined) return true;
      if (step.ramp?.startFtpPercent !== undefined) return true;
      if (step.steps && step.steps.length > 0) return hasFtpBasedSteps(step.steps);
      return false;
    });
  };

  // Determine if this is indoor cycling (uses FTP% instead of zones)
  const isIndoorCycling =
    sportType === 'cycling' && (environment === 'indoor' || hasFtpBasedSteps(steps));

  const { flatSteps, totalDuration } = useMemo(() => {
    const flat = flattenSteps(steps, isIndoorCycling);
    const total = flat.reduce((sum, step) => sum + step.duration, 0);
    return { flatSteps: flat, totalDuration: total };
  }, [steps, isIndoorCycling]);

  if (flatSteps.length === 0 || totalDuration === 0) {
    // Empty or rest workout - show flat line
    return (
      <Box
        sx={{
          width: '100%',
          height,
          backgroundColor: theme.palette.action.hover,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '80%',
            height: 2,
            backgroundColor: theme.palette.divider,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height,
        position: 'relative',
      }}
      aria-label="Workout zone profile visualization"
    >
      {/* FTP reference line for indoor cycling (100% FTP = 71.4% height based on ftpPercentToHeight formula) */}
      {isIndoorCycling && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${ftpPercentToHeight(100)}%`,
            height: '2px',
            borderBottom: `2px dashed ${theme.palette.warning.main}`,
            zIndex: 1,
            pointerEvents: 'none',
          }}
          title="FTP (100%)"
        />
      )}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '1px',
          backgroundColor: theme.palette.background.default,
          borderRadius: 1,
          overflow: 'hidden',
          padding: '2px',
        }}
      >
        {flatSteps.map((step, index) => {
          const widthPercent = (step.duration / totalDuration) * 100;
          const isHovered = hoveredStepIndex === index;

          // Common hover handlers
          const hoverHandlers = {
            onMouseEnter: () => onHoverStep?.(index),
            onMouseLeave: () => onHoverStep?.(null),
          };

          // Handle FTP-based steps (indoor cycling)
          if (step.ftpPercent !== undefined) {
            const heightPercent = ftpPercentToHeight(step.ftpPercent);
            const color = getFtpColor(step.ftpPercent);

            // FTP ramp segment
            if (step.isRamp && step.rampEndFtpPercent !== undefined) {
              const nextHeightPercent = ftpPercentToHeight(step.rampEndFtpPercent);
              const nextColor = getFtpColor(step.rampEndFtpPercent);

              // Use clip-path to create diagonal slope
              const clipPath = `polygon(0 ${100 - heightPercent}%, 100% ${100 - nextHeightPercent}%, 100% 100%, 0 100%)`;

              return (
                <Tooltip
                  key={index}
                  title={getTooltipContent(step, true, step.rampEndFtpPercent)}
                  arrow
                  placement="top"
                  slotProps={{
                    tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'center' } },
                  }}
                >
                  <Box
                    {...hoverHandlers}
                    sx={{
                      width: `${widthPercent}%`,
                      minWidth: '4px',
                      height: '100%',
                      position: 'relative',
                      cursor: 'pointer',
                      opacity: isHovered ? 1 : undefined,
                      filter: isHovered ? 'brightness(1.2)' : undefined,
                      '&:hover': {
                        filter: 'brightness(1.2)',
                      },
                    }}
                  >
                    {/* Diagonal ramp with gradient */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '100%',
                        background: `linear-gradient(to right, ${color}, ${nextColor})`,
                        clipPath: clipPath,
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            }

            // Regular FTP step
            return (
              <Tooltip
                key={index}
                title={getTooltipContent(step)}
                arrow
                placement="top"
                slotProps={{
                  tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'center' } },
                }}
              >
                <Box
                  {...hoverHandlers}
                  sx={{
                    width: `${widthPercent}%`,
                    minWidth: '2px',
                    height: `${heightPercent}%`,
                    background: color,
                    borderRadius: '2px 2px 0 0',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    filter: isHovered ? 'brightness(1.2)' : undefined,
                    '&:hover': {
                      filter: 'brightness(1.2)',
                    },
                  }}
                />
              </Tooltip>
            );
          }

          // Zone-based steps (running, outdoor cycling)
          const heightPercent = ZONE_HEIGHTS[step.zone] || 40;
          const color = ZONE_COLORS[step.zone as keyof typeof ZONE_COLORS] || ZONE_COLORS[2];

          // For ramp segments, create a diagonal slope effect
          const isRampSegment = step.isRamp && step.rampEndZone;

          if (isRampSegment && step.rampEndZone) {
            // Calculate next zone height for the slope
            const isRampUp = step.rampEndZone > step.zone;
            const nextZone = isRampUp ? step.zone + 1 : step.zone - 1;
            const nextHeightPercent = ZONE_HEIGHTS[nextZone] || heightPercent;
            const nextColor = ZONE_COLORS[nextZone as keyof typeof ZONE_COLORS] || color;

            // Use clip-path to create diagonal slope
            // For ramp up: bottom-left to top-right
            // For ramp down: top-left to bottom-right
            const clipPath = isRampUp
              ? `polygon(0 ${100 - heightPercent}%, 100% ${100 - nextHeightPercent}%, 100% 100%, 0 100%)`
              : `polygon(0 ${100 - heightPercent}%, 100% ${100 - nextHeightPercent}%, 100% 100%, 0 100%)`;

            return (
              <Tooltip
                key={index}
                title={getTooltipContent(step, true)}
                arrow
                placement="top"
                slotProps={{
                  tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'center' } },
                }}
              >
                <Box
                  {...hoverHandlers}
                  sx={{
                    width: `${widthPercent}%`,
                    minWidth: '4px',
                    height: '100%',
                    position: 'relative',
                    cursor: 'pointer',
                    filter: isHovered ? 'brightness(1.2)' : undefined,
                    '&:hover': {
                      filter: 'brightness(1.2)',
                    },
                  }}
                >
                  {/* Diagonal ramp with gradient */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '100%',
                      background: `linear-gradient(to right, ${color}, ${nextColor})`,
                      clipPath: clipPath,
                    }}
                  />
                </Box>
              </Tooltip>
            );
          }

          return (
            <Tooltip
              key={index}
              title={getTooltipContent(step)}
              arrow
              placement="top"
              slotProps={{
                tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'center' } },
              }}
            >
              <Box
                {...hoverHandlers}
                sx={{
                  width: `${widthPercent}%`,
                  minWidth: '2px',
                  height: `${heightPercent}%`,
                  background: color,
                  borderRadius: '2px 2px 0 0',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  filter: isHovered ? 'brightness(1.2)' : undefined,
                  '&:hover': {
                    filter: 'brightness(1.2)',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default WorkoutZoneProfile;
